// client/src/redux/Slices/BillingSlice.ts
//
// Account-level view of every stream subscription (GET /billing/subscriptions,
// polled by DashboardLayout) plus the per-stream subscription actions. After
// any per-stream mutation the stream's entitlement and the list are refreshed.
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import billingApiInstance from "../../api/BillingApi";
import streamSubscriptionApiInstance from "../../api/StreamSubscriptionApi";
import trialApiInstance from "../../api/TrialApi";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import {
  beginCommand,
  bindCommandOperation,
  checkoutCommandScope,
  finishCommand,
  finishOlderCommands,
  getCommand,
} from "../../helpers/pendingCommand";
import { refreshStreamSnapshot } from "../refreshStreamSnapshot";
import { getMe } from "./AuthSlice";
import type {
  CheckoutOperationView,
  BillingOperationSummary,
  DowngradeMutationResult,
  DowngradeBody,
  PlanCheckoutBody,
  RetryAllPaymentsResult,
  SinglePaymentRetryResult,
  StreamPlan,
  StorageMutationResult,
  SubscriptionListItem,
} from "../../types/billing";
import type { RootState } from "../store";
import { upsertStreamOperation } from "./OperationsSlice";

interface BillingState {
  subscriptions: SubscriptionListItem[];
  subscriptionsLoaded: boolean;
  subscriptionsLoading: boolean;
  subscriptionsError: string | null;
  checkoutLoadingPlan: StreamPlan | null;
  portalLoading: boolean;
  retryAllLoading: boolean;
  retryAllResult: RetryAllPaymentsResult | null;
  paymentRetryByStreamId: Record<string, SinglePaymentRetryResult | undefined>;
  activateTrialLoading: boolean;
  /** Any in-flight stream action, retained for controls outside the subscription panel. */
  streamActionLoading: Record<string, boolean>;
  /** In-flight request counts keyed by stream and the control action that owns them. */
  streamActionPendingByStreamId: Record<string, Record<string, number>>;
  pendingActionByStreamId: Record<
    string,
    { operationId: string; action: string } | undefined
  >;
  /** A32: Stripe hosted-invoice URL from the last 3DS request, per stream. */
  paymentActionUrlByStreamId: Record<string, string>;
}

const initialState: BillingState = {
  subscriptions: [],
  subscriptionsLoaded: false,
  subscriptionsLoading: false,
  subscriptionsError: null,
  checkoutLoadingPlan: null,
  portalLoading: false,
  retryAllLoading: false,
  retryAllResult: null,
  paymentRetryByStreamId: {},
  activateTrialLoading: false,
  streamActionLoading: {},
  streamActionPendingByStreamId: {},
  pendingActionByStreamId: {},
  paymentActionUrlByStreamId: {},
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    setSubscriptions(state, action: PayloadAction<SubscriptionListItem[]>) {
      state.subscriptions = action.payload;
      state.subscriptionsLoaded = true;
    },
    setSubscriptionsLoading(state, action: PayloadAction<boolean>) {
      state.subscriptionsLoading = action.payload;
    },
    setSubscriptionsError(state, action: PayloadAction<string | null>) {
      state.subscriptionsError = action.payload;
    },
    setCheckoutLoading(state, action: PayloadAction<StreamPlan | null>) {
      state.checkoutLoadingPlan = action.payload;
    },
    setPortalLoading(state, action: PayloadAction<boolean>) {
      state.portalLoading = action.payload;
    },
    setRetryAllLoading(state, action: PayloadAction<boolean>) {
      state.retryAllLoading = action.payload;
    },
    setRetryAllResult(state, action: PayloadAction<RetryAllPaymentsResult | null>) {
      state.retryAllResult = action.payload;
    },
    setPaymentRetryResult(state, action: PayloadAction<SinglePaymentRetryResult>) {
      state.paymentRetryByStreamId[action.payload.liveStreamId] = action.payload;
    },
    setActivateTrialLoading(state, action: PayloadAction<boolean>) {
      state.activateTrialLoading = action.payload;
    },
    setStreamActionLoading(state, action: PayloadAction<{ streamId: string; loading: boolean }>) {
      state.streamActionLoading[action.payload.streamId] = action.payload.loading;
    },
    setStreamActionPending(
      state,
      action: PayloadAction<{ streamId: string; action: string; delta: 1 | -1 }>
    ) {
      const pending =
        state.streamActionPendingByStreamId[action.payload.streamId] ?? {};
      const count =
        (pending[action.payload.action] ?? 0) + action.payload.delta;
      if (count > 0) pending[action.payload.action] = count;
      else delete pending[action.payload.action];
      if (Object.keys(pending).length) {
        state.streamActionPendingByStreamId[action.payload.streamId] = pending;
        state.streamActionLoading[action.payload.streamId] = true;
      } else {
        delete state.streamActionPendingByStreamId[action.payload.streamId];
        state.streamActionLoading[action.payload.streamId] = false;
      }
    },
    setPendingBillingAction(
      state,
      action: PayloadAction<{
        streamId: string;
        pending: { operationId: string; action: string } | null;
      }>,
    ) {
      if (action.payload.pending)
        state.pendingActionByStreamId[action.payload.streamId] =
          action.payload.pending;
      else delete state.pendingActionByStreamId[action.payload.streamId];
    },
    setPaymentActionUrl(
      state,
      action: PayloadAction<{ streamId: string; url: string | null }>,
    ) {
      const { streamId, url } = action.payload;
      // Only an https page (Stripe's hosted invoice) is ever linked.
      if (url && /^https:\/\//.test(url))
        state.paymentActionUrlByStreamId[streamId] = url;
      else delete state.paymentActionUrlByStreamId[streamId];
    },
  },
});

export const {
  setSubscriptions,
  setSubscriptionsLoading,
  setSubscriptionsError,
  setCheckoutLoading,
  setPortalLoading,
  setRetryAllLoading,
  setRetryAllResult,
  setPaymentRetryResult,
  setActivateTrialLoading,
  setStreamActionLoading,
  setStreamActionPending,
  setPendingBillingAction,
  setPaymentActionUrl,
} = billingSlice.actions;

export const selectSubscriptionForStream = (
  state: RootState,
  streamId: string | undefined,
): SubscriptionListItem | undefined =>
  streamId ? state.billing.subscriptions.find((s) => s.liveStreamId === streamId) : undefined;

export const getSubscriptions = createAsyncThunk(
  "billing/getSubscriptions",
  async (_, { dispatch, getState }) => {
    dispatch(setSubscriptionsLoading(true));
    try {
      const { data } = await billingApiInstance.listSubscriptions();
      dispatch(setSubscriptions(data));
      dispatch(setSubscriptionsError(null));
      return data;
    } catch (err: any) {
      // A33/P8: the 60 s poll toasts once per failure streak, not on every
      // retry. The "already failing" check and the state update that marks
      // the streak failed happen synchronously, with no await between them,
      // so a second poll failing concurrently with this one still sees the
      // streak already marked and skips its own toast.
      const alreadyFailing =
        (getState() as { billing: BillingState }).billing.subscriptionsError !== null;
      dispatch(setSubscriptionsError(err?.message ?? "Failed to load subscriptions"));
      if (!alreadyFailing) dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(setSubscriptionsLoading(false));
    }
  },
);

export const ACCOUNT_OPERATION_POLL_INTERVAL_MS = 2000;
export const ACCOUNT_OPERATION_POLL_ATTEMPTS = 10;

const pollAccountOperation = async (
  operationId: string,
  settled: (view: CheckoutOperationView) => boolean,
): Promise<CheckoutOperationView> => {
  const retried = new Set<string>();
  for (let attempt = 0; attempt < ACCOUNT_OPERATION_POLL_ATTEMPTS; attempt += 1) {
    let data: CheckoutOperationView;
    try {
      data = (await billingApiInstance.getCheckoutOperation(operationId)).data;
    } catch (error) {
      if (attempt + 1 === ACCOUNT_OPERATION_POLL_ATTEMPTS) throw error;
      await new Promise((resolve) =>
        setTimeout(resolve, ACCOUNT_OPERATION_POLL_INTERVAL_MS),
      );
      continue;
    }
    if (settled(data)) return data;

    const retryRequired =
      data.state === "retryable_failed" || data.error === "OPERATION_RETRY_REQUIRED";
    if (retryRequired) {
      const retryOperationId = data.retryOperationId ?? operationId;
      if (!retried.has(retryOperationId)) {
        await billingApiInstance.retryCheckoutOperation(retryOperationId);
        retried.add(retryOperationId);
        continue;
      }
    }

    if (data.state === "failed") {
      throw new Error(data.error ?? "Account recovery failed");
    }
    if (attempt + 1 < ACCOUNT_OPERATION_POLL_ATTEMPTS) {
      await new Promise((resolve) =>
        setTimeout(resolve, ACCOUNT_OPERATION_POLL_INTERVAL_MS),
      );
    }
  }
  throw new Error("Account recovery is still pending");
};

const waitForAccountOperation = (operationId: string) =>
  pollAccountOperation(operationId, (view) => view.state === "completed");

const resolveCheckoutUrl = async (checkout: {
  url: string | null;
  operationId?: string;
}) => {
  if (checkout.url) return checkout;
  if (!checkout.operationId) throw new Error("Checkout is still unavailable");
  const view = await pollAccountOperation(
    checkout.operationId,
    (operation) => operation.url !== null,
  );
  return { url: view.url, operationId: checkout.operationId };
};

type CheckoutResponse = { url: string | null; operationId?: string };
const checkoutInFlight = new Map<string, Promise<CheckoutResponse>>();

const terminalOperation = (state: string) =>
  ["completed", "fulfilled", "cancelled", "superseded", "failed", "expired"].includes(
    state,
  );

const runCheckout = (
  scope: string,
  dispatch: any,
  plan: StreamPlan | null,
  request: (requestKey: string) => Promise<CheckoutResponse>,
): Promise<CheckoutResponse> => {
  const existingFlight = checkoutInFlight.get(scope);
  if (existingFlight) return existingFlight;
  dispatch(setCheckoutLoading(plan));
  const promise = (async () => {
    try {
      const existing = getCommand(scope);
      if (existing?.operationId) {
        try {
          const { data } = await billingApiInstance.getCheckoutOperation(
            existing.operationId,
          );
          if (terminalOperation(data.state))
            finishCommand(scope, { operationId: existing.operationId });
        } catch {
          // Preserve the owned key when its exact operation cannot be read.
        }
      }
      const { requestKey } = beginCommand(scope);
      const checkout = await request(requestKey);
      if (checkout.operationId)
        bindCommandOperation(scope, requestKey, checkout.operationId);
      return await resolveCheckoutUrl(checkout);
    } catch (error) {
      dispatchErrorNotification(error);
      throw error;
    } finally {
      checkoutInFlight.delete(scope);
      dispatch(setCheckoutLoading(null));
    }
  })();
  checkoutInFlight.set(scope, promise);
  return promise;
};

export const createCheckout = createAsyncThunk<CheckoutResponse, StreamPlan>(
  "billing/createCheckout",
  (plan, { dispatch }) => {
    const scope = checkoutCommandScope("checkout", null, plan);
    return runCheckout(scope, dispatch, plan, async (requestKey) =>
      (await billingApiInstance.createCheckout(plan, requestKey)).data,
    );
  },
);

export const openBillingPortal = createAsyncThunk<{ url: string }>(
  "billing/openPortal",
  async (_, { dispatch }) => {
    dispatch(setPortalLoading(true));
    try {
      let { data } = await billingApiInstance.getPortalSession();
      if (!data.url && data.operationId) {
        await waitForAccountOperation(data.operationId);
        data = (await billingApiInstance.getPortalSession()).data;
      }
      if (!data.url) throw new Error("Billing portal is still unavailable");
      return { url: data.url };
    } catch (err: any) {
      dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(setPortalLoading(false));
    }
  },
);

export const retryAllPayments = createAsyncThunk<RetryAllPaymentsResult>(
  "billing/retryAll",
  async (_, { dispatch }) => {
    dispatch(setRetryAllLoading(true));
    try {
      const { data } = await billingApiInstance.retryAll();
      dispatch(setRetryAllResult(data));
      await dispatch(getSubscriptions());
      return data;
    } catch (err: any) {
      dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(setRetryAllLoading(false));
    }
  },
);

export const activateTrial = createAsyncThunk<{ liveStreamId: string }>(
  "billing/activateTrial",
  async (_, { dispatch }) => {
    dispatch(setActivateTrialLoading(true));
    try {
      const { data } = await trialApiInstance.activateTrial();
      // trialUsed flips on the user; the list gains the trial row.
      await Promise.all([dispatch(getMe()), dispatch(getSubscriptions())]);
      return data;
    } catch (err: any) {
      dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(setActivateTrialLoading(false));
    }
  },
);

/**
 * Runs one per-stream subscription call, then refreshes the authoritative
 * snapshot once. Plan changes may reset output settings; draft ownership
 * still decides whether fetched settings initialize the wizard.
 */
async function streamAction<T>(
  streamId: string,
  actionName: string,
  dispatch: any,
  run: () => Promise<T>,
  refresh = true,
  refreshStream = false,
  // A33: false when `run` already toasted its own failure (runCheckout).
  notifyOnError = true
): Promise<T> {
  dispatch(setStreamActionPending({ streamId, action: actionName, delta: 1 }));
  try {
    const result = await run();
    const tasks: Promise<unknown>[] = [];
    if (refresh) {
      tasks.push(dispatch(getSubscriptions()));
    }
    if (refresh || refreshStream) {
      tasks.push(dispatch(refreshStreamSnapshot(streamId)));
    }
    if (tasks.length) await Promise.all(tasks);
    return result;
  } catch (err: any) {
    if (notifyOnError) dispatchErrorNotification(err);
    throw err;
  } finally {
    dispatch(
      setStreamActionPending({ streamId, action: actionName, delta: -1 })
    );
  }
}

async function commandedStreamAction<T extends BillingOperationSummary>(
  scope: string,
  streamId: string,
  dispatch: any,
  run: (requestKey: string) => Promise<T>,
  actionName: string,
  relatedScopePrefixes: string[] = [],
  refresh = true,
  refreshStream = false,
): Promise<T> {
  const { requestKey } = beginCommand(scope);
  const admit = (result: T) => {
    if (!result?.id) return;
    bindCommandOperation(scope, requestKey, result.id);
    dispatch(upsertStreamOperation(result));
    if (terminalOperation(result.state)) {
      finishCommand(scope, { operationId: result.id });
      dispatch(setPendingBillingAction({ streamId, pending: null }));
    } else
      dispatch(
        setPendingBillingAction({
          streamId,
          pending: { operationId: result.id, action: actionName },
        }),
      );
    finishOlderCommands(scope, requestKey, relatedScopePrefixes);
  };
  const result = await streamAction(
    streamId,
    actionName,
    dispatch,
    async () => {
      const admitted = await run(requestKey);
      admit(admitted);
      return admitted;
    },
    refresh,
    refreshStream,
  );
  admit(result);
  return result;
}

export const cancelSubscription = createAsyncThunk<BillingOperationSummary, string>(
  "billing/cancelSubscription",
  (streamId, { dispatch }) =>
    commandedStreamAction(
      `billing:cancel:${streamId}`,
      streamId,
      dispatch,
      async (requestKey) =>
        (await streamSubscriptionApiInstance.cancel(streamId, requestKey)).data,
      "cancel",
      [`billing:cancel:${streamId}`, `billing:uncancel:${streamId}`],
    ),
);

export const uncancelSubscription = createAsyncThunk<BillingOperationSummary, string>(
  "billing/uncancelSubscription",
  (streamId, { dispatch }) =>
    commandedStreamAction(
      `billing:uncancel:${streamId}`,
      streamId,
      dispatch,
      async (requestKey) =>
        (await streamSubscriptionApiInstance.uncancel(streamId, requestKey)).data,
      "uncancel",
      [`billing:cancel:${streamId}`, `billing:uncancel:${streamId}`],
    ),
);

export const setStorageBlocks = createAsyncThunk<
  StorageMutationResult,
  { streamId: string; blocks: number }
>("billing/setStorageBlocks", ({ streamId, blocks }, { dispatch }) =>
  commandedStreamAction(
    `billing:storage:${streamId}:${blocks}`,
    streamId,
    dispatch,
    async (requestKey) =>
      (await streamSubscriptionApiInstance.setStorageBlocks(streamId, blocks, requestKey)).data,
    "storage",
    [`billing:storage:${streamId}:`],
  ),
);

export const upgradePlan = createAsyncThunk<BillingOperationSummary, { streamId: string; plan: StreamPlan }>(
  "billing/upgradePlan",
  ({ streamId, plan }, { dispatch }) =>
    commandedStreamAction(
      `billing:upgrade:${streamId}:${plan}`,
      streamId,
      dispatch,
      async (requestKey) =>
        (await streamSubscriptionApiInstance.upgrade(streamId, plan, requestKey)).data,
      "upgrade",
      [`billing:upgrade:${streamId}:`, `billing:downgrade:${streamId}:`],
      true,
      true,
    ),
);

export const scheduleDowngrade = createAsyncThunk<
  DowngradeMutationResult,
  { streamId: string } & DowngradeBody
>("billing/scheduleDowngrade", ({ streamId, ...body }, { dispatch }) =>
  commandedStreamAction(
    `billing:downgrade:${streamId}:${body.blocks}:${body.acknowledgeQualityLoss === true}`,
    streamId,
    dispatch,
    async (requestKey) =>
      (await streamSubscriptionApiInstance.downgrade(streamId, body, requestKey)).data,
    "downgrade",
    [`billing:upgrade:${streamId}:`, `billing:downgrade:${streamId}:`],
    true,
    true,
  ),
);

export const cancelDowngrade = createAsyncThunk<BillingOperationSummary, string>(
  "billing/cancelDowngrade",
  (streamId, { dispatch }) =>
    commandedStreamAction(
      `billing:cancel-downgrade:${streamId}`,
      streamId,
      dispatch,
      async (requestKey) =>
        (await streamSubscriptionApiInstance.cancelDowngrade(streamId, requestKey)).data,
      "cancel_downgrade",
      [`billing:downgrade:${streamId}:`, `billing:cancel-downgrade:${streamId}`],
      true,
      true,
    ),
);

/** Returns the Stripe url; the caller redirects. Also repopulates the stream
 * (silent: false) so the form and header badge aren't stale if the checkout
 * flow ends up not navigating away (e.g. an ad/popup blocker). */
export const resubscribe = createAsyncThunk<
  { url: string | null; operationId?: string },
  { streamId: string } & PlanCheckoutBody
>("billing/resubscribe", ({ streamId, ...body }, { dispatch }) =>
  streamAction(
    streamId,
    "resubscribe",
    dispatch,
    () =>
      runCheckout(
        checkoutCommandScope(
          "resubscribe",
          streamId,
          body.plan,
          body.blocks,
          body.acknowledgeQualityLoss,
        ),
        dispatch,
        null,
        async (requestKey) =>
          (
            await streamSubscriptionApiInstance.resubscribe(
              streamId,
              body,
              requestKey,
            )
          ).data,
      ),
    false,
    true,
    false,
  ),
);

export const convertTrial = createAsyncThunk<
  { url: string | null; operationId?: string },
  { streamId: string } & PlanCheckoutBody
>("billing/convertTrial", ({ streamId, ...body }, { dispatch }) =>
  streamAction(
    streamId,
    "convert",
    dispatch,
    () =>
      runCheckout(
        checkoutCommandScope(
          "convert",
          streamId,
          body.plan,
          body.blocks,
          body.acknowledgeQualityLoss,
        ),
        dispatch,
        null,
        async (requestKey) =>
          (
            await streamSubscriptionApiInstance.convert(
              streamId,
              body,
              requestKey,
            )
          ).data,
      ),
    false,
    true,
    false,
  ),
);

export const retryPayment = createAsyncThunk<SinglePaymentRetryResult, string>(
  "billing/retryPayment",
  (streamId, { dispatch }) =>
    streamAction(streamId, "retry_payment", dispatch, async () => {
      const { data } = await streamSubscriptionApiInstance.retryPayment(streamId);
      dispatch(setPaymentRetryResult(data));
      return data;
    }),
);

export default billingSlice.reducer;
